import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { environment } from "../../config/environment";
import { customFetch } from "../../utils/Auth";

export const LAMBDA_RUN_STATUS = {
  ERROR: "error",
  WARNING: "warning",
  SUCCESS: "success",
  RUNNING: "running",
  UNKNOWN: "unknown",
};

const TERMINAL_STATUSES = new Set([
  LAMBDA_RUN_STATUS.ERROR,
  LAMBDA_RUN_STATUS.WARNING,
  LAMBDA_RUN_STATUS.SUCCESS,
]);

const POLL_INTERVAL_MS = 3000;
// ~2 minutes. Past this the run is not a quick one, and the user can still
// refresh by hand rather than us polling CloudWatch indefinitely.
const POLL_MAX_ATTEMPTS = 40;

// Worst first, mirroring the server's ordering so both agree on an aggregate.
const SEVERITY_ORDER = [
  LAMBDA_RUN_STATUS.ERROR,
  LAMBDA_RUN_STATUS.WARNING,
  LAMBDA_RUN_STATUS.RUNNING,
  LAMBDA_RUN_STATUS.UNKNOWN,
  LAMBDA_RUN_STATUS.SUCCESS,
];

/** Collapse several runs into the single most severe status. */
export const worstStatus = (statuses = []) => {
  const present = statuses.filter(Boolean);
  if (present.length === 0) return LAMBDA_RUN_STATUS.UNKNOWN;
  return (
    SEVERITY_ORDER.find((status) => present.includes(status)) ||
    LAMBDA_RUN_STATUS.UNKNOWN
  );
};

export async function fetchLambdaRunLogs({ requestId, functionName, since }) {
  const params = new URLSearchParams({ requestId, functionName });
  if (since) params.set("since", String(since));

  const response = await customFetch(
    `${environment.apiUrl}/oncall/lambda-run-logs?${params}`,
    { method: "GET" }
  );
  if (!response.ok) throw new Error("Failed to fetch Lambda run logs");
  return response.json();
}

const splitTailLogs = (tailLogs) =>
  String(tailLogs || "")
    .split("\n")
    .filter((message) => message.trim())
    .map((message) => ({ timestamp: null, message }));

const initialStateFor = (run) => ({
  status: run.initialStatus || LAMBDA_RUN_STATUS.RUNNING,
  // A synchronous invoke already returned up to 4KB of log; show it until
  // CloudWatch catches up with the complete output.
  lines: splitTailLogs(run.initialLogs),
  finished: false,
  unavailable: false,
  durationMs: null,
  error: null,
});

/**
 * Polls CloudWatch for one or more Lambda invocations until each finishes.
 *
 * Takes the whole set at once rather than one hook per run, so the caller can
 * render a combined status without breaking the rules of hooks, and so polling
 * continues even when a log panel is collapsed or its dialog is closed.
 *
 * Uses customFetch rather than makeApiCall because a poll can outlive a Cognito
 * access token and only customFetch refreshes it.
 *
 * @param {Array<{functionKey?: string, functionName: string, requestId: string,
 *   startedAt?: number, initialStatus?: string, initialLogs?: string}>} runs
 */
export function useLambdaRuns(runs = []) {
  const [statesByRequestId, setStatesByRequestId] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  // Identity of the run set; recreating the array each render must not restart
  // polling, but a genuinely new invocation must.
  const signature = runs
    .map((run) => `${run.requestId || ""}:${run.functionName || ""}`)
    .join("|");

  const runsRef = useRef(runs);
  runsRef.current = runs;

  const attemptsRef = useRef(0);
  const timerRef = useRef(null);
  // Monotonically increasing epoch. Every new polling loop (from the effect or
  // from a manual refresh) bumps this. A tick whose captured epoch is stale
  // knows a newer loop superseded it and stops, preventing two loops from
  // running concurrently against the shared attempt counter.
  const epochRef = useRef(0);

  const pollOnce = useCallback(async () => {
    const pending = runsRef.current.filter(
      (run) => run.requestId && run.functionName
    );
    if (pending.length === 0) return true;

    const results = await Promise.all(
      pending.map(async (run) => {
        try {
          const data = await fetchLambdaRunLogs({
            requestId: run.requestId,
            functionName: run.functionName,
            since: run.startedAt,
          });
          return { run, data };
        } catch (err) {
          return { run, error: err.message };
        }
      })
    );

    setStatesByRequestId((current) => {
      const next = { ...current };
      for (const { run, data, error } of results) {
        const previous = next[run.requestId] || initialStateFor(run);
        if (error) {
          next[run.requestId] = { ...previous, error };
          continue;
        }
        next[run.requestId] = {
          status: data.status || LAMBDA_RUN_STATUS.UNKNOWN,
          lines: data.lines?.length ? data.lines : previous.lines,
          finished: Boolean(data.finished),
          unavailable: Boolean(data.unavailable),
          durationMs: data.durationMs ?? null,
          error: null,
        };
      }
      return next;
    });

    return results.every(
      ({ data }) =>
        data && (Boolean(data.unavailable) || TERMINAL_STATUSES.has(data.status))
    );
  }, []);

  const startPollingLoop = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const myEpoch = ++epochRef.current;
    attemptsRef.current = 0;

    const tick = async () => {
      if (epochRef.current !== myEpoch) return;
      attemptsRef.current += 1;
      const done = await pollOnce();
      if (epochRef.current !== myEpoch) return;
      if (done || attemptsRef.current >= POLL_MAX_ATTEMPTS) return;
      timerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    };

    tick();
  }, [pollOnce]);

  useEffect(() => {
    const seeded = {};
    for (const run of runsRef.current) {
      if (run.requestId) seeded[run.requestId] = initialStateFor(run);
    }
    setStatesByRequestId(seeded);

    const trackable = runsRef.current.filter(
      (run) => run.requestId && run.functionName
    );
    if (trackable.length === 0) return undefined;

    startPollingLoop();

    return () => {
      // Bump epoch so any in-flight tick from this loop stops itself.
      epochRef.current += 1;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, startPollingLoop]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      // startPollingLoop bumps the epoch, which kills any prior loop — even
      // one whose pollOnce is still in flight.
      startPollingLoop();
    } finally {
      setRefreshing(false);
    }
  }, [startPollingLoop]);

  const resolved = useMemo(
    () =>
      runs.map((run) => ({
        ...run,
        ...(statesByRequestId[run.requestId] || initialStateFor(run)),
      })),
    [runs, statesByRequestId]
  );

  return {
    runs: resolved,
    status: worstStatus(resolved.map((run) => run.status)),
    // Only meaningful for a single run; the combined view shows per-run timings.
    durationMs: resolved.length === 1 ? resolved[0].durationMs : null,
    refresh,
    refreshing,
  };
}
